import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { painPointApi, winApi, skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import type { PainPoint, Win, Skill, SkillLevel } from '@/types/skillKnowledge';

const SKILL_LEVELS: SkillLevel[] = ['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'];

const painPointAssociatedSkillSchema = z.object({
  skillId: z.string().min(1, 'Skill ID is required'),
  proficiencyLevel: z.enum(['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY']),
});

const painPointSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(200, 'Name must be less than 200 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(2000, 'Description must be less than 2000 characters'),
  summary: z.string().max(500, 'Summary must be less than 500 characters').optional(),
  successCriteria: z.string().min(10, 'Success criteria must be at least 10 characters').max(1000, 'Success criteria must be less than 1000 characters'), // Required by API
  winIds: z.array(z.string()).optional(),
  associatedSkills: z.array(painPointAssociatedSkillSchema).optional(),
  isActive: z.boolean(),
});

type PainPointFormData = z.infer<typeof painPointSchema>;

export const PainPointFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [wins, setWins] = useState<Win[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);

  const form = useForm<PainPointFormData>({
    resolver: zodResolver(painPointSchema),
      defaultValues: {
        name: '',
        description: '',
        summary: '',
        successCriteria: '', // Required by API
        winIds: [],
        associatedSkills: [],
        isActive: true,
      },
  });

  const { fields: associatedSkillFields, append: appendAssociatedSkill, remove: removeAssociatedSkill } = useFieldArray({
    control: form.control,
    name: 'associatedSkills',
  });

  useEffect(() => {
    // Load wins and skills for dropdowns
    Promise.all([
      winApi.list({ page: 0, size: 1000, isActive: true }).then((response) => response.content || []),
      skillApi.list({ page: 0, size: 1000 }).then((response) => response.content || []),
    ]).then(([winsData, skillsData]) => {
      setWins(winsData);
      setSkills(skillsData);
    }).catch((error) => {
      console.error('Error loading data:', error);
    });
  }, []);

  // Fetch pain point data for edit mode
  const {
    data: _painPoint,
    isLoading: isLoadingPainPoint,
    error: painPointError,
  } = useEntity<PainPoint>({
    fetchFn: (painPointId: string) => painPointApi.getById(painPointId),
    entityId: id,
    entityName: 'painPoint',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        name: data.name,
        description: data.description,
        summary: data.summary || '',
        successCriteria: data.successCriteria || '',
        winIds: data.winIds || [],
        associatedSkills: data.associatedSkills || [],
        isActive: data.isActive ?? true,
      });
    },
    onError: () => {
      navigate('/admin/pain-points');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: PainPointFormData) => {
      const payload: Partial<PainPoint> = {
        name: data.name,
        description: data.description,
        summary: data.summary,
        successCriteria: data.successCriteria,
        winIds: data.winIds?.filter(Boolean) || [],
        associatedSkills: data.associatedSkills || [],
        isActive: data.isActive,
      };
      return await painPointApi.create(payload as Omit<PainPoint, 'id' | 'createdAt' | 'updatedAt'>);
    },
    entityName: 'painPoint',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/pain-points');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PainPointFormData) => {
      if (!id) throw new Error('Pain Point ID is required');
      const payload: Partial<PainPoint> = {
        name: data.name,
        description: data.description,
        summary: data.summary,
        successCriteria: data.successCriteria,
        winIds: data.winIds?.filter(Boolean) || [],
        associatedSkills: data.associatedSkills || [],
        isActive: data.isActive,
      };
      return await painPointApi.update(id, payload);
    },
    entityName: 'painPoint',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/pain-points');
    },
  });

  const onSubmit = async (data: PainPointFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingPainPoint;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading pain point..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && painPointError) {
    return <ErrorState error={painPointError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/pain-points">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Pain Point' : 'Create Pain Point'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update pain point information' : 'Create a new pain point'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the pain point</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter pain point name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter pain point description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter pain point summary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="successCriteria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Success Criteria *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter success criteria"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <FormDescription>Mark this pain point as active</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Related Wins</CardTitle>
              <CardDescription>Select wins (achievements/milestones) related to this pain point</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="winIds"
                render={() => (
                  <FormItem>
                    <div className="space-y-2">
                      {wins.map((win) => (
                        <FormField
                          key={win.id}
                          control={form.control}
                          name="winIds"
                          render={({ field }) => {
                            return (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <input
                                    type="checkbox"
                                    checked={field.value?.includes(win.id || '')}
                                    onChange={(e) => {
                                      const current = field.value || [];
                                      if (e.target.checked) {
                                        field.onChange([...current, win.id]);
                                      } else {
                                        field.onChange(current.filter((id) => id !== win.id));
                                      }
                                    }}
                                    className="h-4 w-4"
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal">
                                  {win.name}
                                  {win.summary && (
                                    <span className="text-muted-foreground ml-2">- {win.summary}</span>
                                  )}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Directly Associated Skills</CardTitle>
              <CardDescription>Define skills directly associated with this pain point</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {associatedSkillFields.map((field, index) => (
                <div key={field.id} className="grid gap-4 md:grid-cols-[1fr_auto_auto] items-end">
                  <FormField
                    control={form.control}
                    name={`associatedSkills.${index}.skillId`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Skill</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select skill" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {skills.map((skill) => (
                              <SelectItem key={skill.id} value={skill.id || ''}>
                                {skill.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`associatedSkills.${index}.proficiencyLevel`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Level</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SKILL_LEVELS.map((level) => (
                              <SelectItem key={level} value={level}>
                                {level.replace(/_/g, ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeAssociatedSkill(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => appendAssociatedSkill({ skillId: '', proficiencyLevel: 'BASIC' })}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Associated Skill
              </Button>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/pain-points">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PainPointFormPage;







